import React, { FC, useEffect, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import {
  DataGridPro,
  GridColDef,
  GridRenderCellParams,
  GridScrollParams,
  GridValidRowModel,
  useGridApiContext,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import isNil from 'lodash/isNil';

import { useTimesheetEdit } from 'src/features/timesheets/providers';
import { getStaticColumnWidth } from 'src/shared/components';
import { useTranslate } from 'src/shared/providers';

import { TimesheetExtraTimeCell } from './TimesheetExtraTimeCell';
import {
  TimesheetColumnEnums,
  TimesheetExtraColumnEnums,
  TimesheetExtraRowEnums,
  TimesheetTotalColumnEnums,
  TimesheetTotalRowEnums,
} from './TimesheetGrid.enums';
import { TimesheetGrid } from './TimesheetGrid.types';
import { TIMESHEET_ROW_HEIGHT } from './TimesheetGrid.utils';
import { TimesheetTotalDurationCell } from './TimesheetTotalDurationCell';
import { TimesheetTotalExpenseCell } from './TimesheetTotalExpenseCell';

type TimesheetTotalsRowType = {
  id: string;
  timesheet: TimesheetGrid | undefined;
  [TimesheetTotalColumnEnums.TOTAL_ROW_NAME]: string;
  [TimesheetTotalColumnEnums.GRAND_TOTAL_HOURS]: number | undefined;
  [TimesheetTotalColumnEnums.GRAND_TOTAL_EXPENSES]: number | undefined;
};

type TimesheetExtraRowType = {
  id: string;
  [TimesheetExtraColumnEnums.EXTRA_ROW_NAME]: string;
};

interface Props {
  leftColumnFields?: TimesheetColumnEnums[];
  rightColumnFields?: TimesheetColumnEnums[];
  hasExpensesColumn?: boolean;
}

export const TimesheetGridFooter: FC<Props> = ({
  leftColumnFields = [],
  rightColumnFields = [],
  hasExpensesColumn = false,
}) => {
  const { t } = useTranslate();

  const totalApiRef = useGridApiRef();
  const extraApiRef = useGridApiRef();
  const mainApiRef = useGridApiContext();

  const { editingTimesheet } = useTimesheetEdit();

  const allColumns = mainApiRef.current.getAllColumns();

  const hasTotalExpensesRow = useMemo(
    () => !!editingTimesheet?.expenseTotals.some((expenseTotal) => expenseTotal.expenseAmount !== 0),
    [editingTimesheet],
  );

  const hasExtraTimeRow = useMemo(
    () =>
      !!editingTimesheet?.columns.some(
        (column) => !isNil(column.overtimeDurationInSeconds) || !isNil(column.isAuthorizedOvertime),
      ),
    [editingTimesheet],
  );
  const rowNameColumnWidth = useMemo(
    () => getStaticColumnWidth(allColumns, leftColumnFields),
    [allColumns, leftColumnFields],
  );

  const durationColumnWidth = useMemo(
    () =>
      getStaticColumnWidth(
        allColumns,
        rightColumnFields.filter((rightColumnField) => rightColumnField === TimesheetColumnEnums.TOTAL_HOURS),
      ),
    [allColumns, rightColumnFields],
  );

  const expensesColumnWidth = useMemo(
    () =>
      getStaticColumnWidth(
        allColumns,
        rightColumnFields.filter((rightColumnField) => rightColumnField === TimesheetColumnEnums.TOTAL_EXPENSES),
      ),
    [allColumns, rightColumnFields],
  );

  const totalColumns: GridColDef<TimesheetTotalsRowType>[] = useMemo(() => {
    const expensesColumn = hasExpensesColumn
      ? [
          {
            field: TimesheetTotalColumnEnums.GRAND_TOTAL_EXPENSES,
            minWidth: expensesColumnWidth,
            maxWidth: expensesColumnWidth,
            flex: 1,
            renderCell: ({ value }: GridRenderCellParams<TimesheetTotalsRowType, number | undefined>) =>
              !!value && <TimesheetTotalExpenseCell expense={value} />,
          },
        ]
      : [];

    return [
      {
        field: TimesheetTotalColumnEnums.TOTAL_ROW_NAME,
        minWidth: rowNameColumnWidth,
        maxWidth: rowNameColumnWidth,
        flex: 1,
        renderCell: ({ value }: GridRenderCellParams<TimesheetTotalsRowType, string>) => (
          <Typography fontSize="0.875rem" fontWeight={600}>
            {value}
          </Typography>
        ),
      },
      ...(editingTimesheet?.columns ?? []).map((column) => {
        const computedWidth = allColumns.find(({ field }) => field === column.date)?.computedWidth;
        return {
          field: column.date,
          minWidth: computedWidth,
          maxWidth: computedWidth,
          flex: 1,
          renderCell: ({ id, field, row }: GridRenderCellParams<TimesheetTotalsRowType, undefined>) => {
            const durationTotal = row.timesheet?.durationTotals.find((durationTotal) => durationTotal.date === field);
            const expenseTotal = row.timesheet?.expenseTotals.find((expenseTotal) => expenseTotal.date === field);

            if (id === TimesheetTotalRowEnums.TOTAL_HOURS) {
              return !!durationTotal?.duration && <TimesheetTotalDurationCell duration={durationTotal.duration} />;
            }

            if (id === TimesheetTotalRowEnums.TOTAL_EXPENSES) {
              return (
                !!expenseTotal?.expenseAmount && <TimesheetTotalExpenseCell expense={expenseTotal.expenseAmount} />
              );
            }

            return null;
          },
        };
      }),
      {
        field: TimesheetTotalColumnEnums.GRAND_TOTAL_HOURS,
        minWidth: durationColumnWidth,
        maxWidth: durationColumnWidth,
        flex: 1,
        renderCell: ({ value }: GridRenderCellParams<TimesheetTotalsRowType, number | undefined>) =>
          !!value && <TimesheetTotalDurationCell duration={value} />,
      },
      ...expensesColumn,
    ];
  }, [allColumns, editingTimesheet, rowNameColumnWidth, durationColumnWidth, expensesColumnWidth, hasExpensesColumn]);

  const extraColumns: GridColDef<TimesheetExtraRowType>[] = useMemo(() => {
    const expensesColumn = hasExpensesColumn
      ? [
          {
            field: TimesheetTotalColumnEnums.GRAND_TOTAL_EXPENSES,
            minWidth: expensesColumnWidth,
            maxWidth: expensesColumnWidth,
            flex: 1,
            renderCell: () => null,
          },
        ]
      : [];

    return [
      {
        field: TimesheetExtraColumnEnums.EXTRA_ROW_NAME,
        minWidth: rowNameColumnWidth,
        maxWidth: rowNameColumnWidth,
        flex: 1,
        renderCell: ({ value }: GridRenderCellParams<TimesheetExtraRowType, string>) => (
          <Typography fontSize="0.875rem" fontWeight={600}>
            {value}
          </Typography>
        ),
      },
      ...(editingTimesheet?.columns ?? []).map((column) => {
        const computedWidth = allColumns.find(({ field }) => field === column.date)?.computedWidth;
        return {
          field: column.date,
          minWidth: computedWidth,
          maxWidth: computedWidth,
          flex: 1,
          renderCell: () => {
            return (
              <TimesheetExtraTimeCell
                duration={column.overtimeDurationInSeconds}
                isAuthorized={column.isAuthorizedOvertime}
              />
            );
          },
        };
      }),
      {
        field: TimesheetTotalColumnEnums.GRAND_TOTAL_HOURS,
        minWidth: durationColumnWidth,
        maxWidth: durationColumnWidth,
        flex: 1,
        renderCell: ({ value }: GridRenderCellParams<TimesheetExtraRowType, number | undefined>) =>
          !!value && <TimesheetTotalDurationCell duration={value} />,
      },
      ...expensesColumn,
    ];
  }, [allColumns, editingTimesheet, rowNameColumnWidth, durationColumnWidth, expensesColumnWidth, hasExpensesColumn]);

  const totalRows = useMemo<TimesheetTotalsRowType[]>(() => {
    const totalExpensesRow = hasTotalExpensesRow
      ? [
          {
            id: TimesheetTotalRowEnums.TOTAL_EXPENSES,
            timesheet: editingTimesheet,
            [TimesheetTotalColumnEnums.TOTAL_ROW_NAME]: t('timesheets.totalExpenses'),
            [TimesheetTotalColumnEnums.GRAND_TOTAL_HOURS]: undefined,
            [TimesheetTotalColumnEnums.GRAND_TOTAL_EXPENSES]: editingTimesheet?.expenseTotals.reduce(
              (expenseGrandTotal, expenseTotal) => expenseGrandTotal + expenseTotal.expenseAmount,
              0,
            ),
          },
        ]
      : [];

    return [
      {
        id: TimesheetTotalRowEnums.TOTAL_HOURS,
        timesheet: editingTimesheet,
        [TimesheetTotalColumnEnums.TOTAL_ROW_NAME]: t('timesheets.totalHours'),
        [TimesheetTotalColumnEnums.GRAND_TOTAL_HOURS]: editingTimesheet?.durationTotals.reduce(
          (durationGrandTotal, durationTotal) => durationGrandTotal + durationTotal.duration,
          0,
        ),
        [TimesheetTotalColumnEnums.GRAND_TOTAL_EXPENSES]: undefined,
      },
      ...totalExpensesRow,
    ];
  }, [editingTimesheet, hasTotalExpensesRow]);

  const extraRows = useMemo<TimesheetExtraRowType[]>(() => {
    return [
      {
        id: TimesheetExtraRowEnums.EXTRA_TIME,
        [TimesheetExtraColumnEnums.EXTRA_ROW_NAME]: t('timesheets.extraTime'),
        [TimesheetExtraColumnEnums.GRAND_TOTAL_HOURS]: editingTimesheet?.columns.reduce(
          (hoursGrandTotal, column) => hoursGrandTotal + (column.overtimeDurationInSeconds ?? 0),
          0,
        ),
      },
    ];
  }, [editingTimesheet]);

  useEffect(() => {
    const handleFooterScroll = ({ left }: GridScrollParams) => {
      mainApiRef.current?.scroll({ left });
      hasExtraTimeRow && extraApiRef.current?.scroll({ left });
    };

    return totalApiRef.current?.subscribeEvent('scrollPositionChange', handleFooterScroll);
  }, [mainApiRef, totalApiRef, extraApiRef, hasExtraTimeRow]);

  useEffect(() => {
    if (!hasExtraTimeRow) {
      return;
    }

    const handleFooterScroll = ({ left }: GridScrollParams) => {
      mainApiRef.current?.scroll({ left });
      totalApiRef.current?.scroll({ left });
    };

    return extraApiRef?.current?.subscribeEvent('scrollPositionChange', handleFooterScroll);
  }, [mainApiRef, totalApiRef, extraApiRef, hasExtraTimeRow]);

  useEffect(() => {
    const handleFooterScroll = ({ left }: GridScrollParams) => {
      const mainScrollPosition = mainApiRef.current.getScrollPosition();

      if (!mainScrollPosition.top && !mainScrollPosition.left) {
        return;
      }

      totalApiRef.current?.scroll({ left });
      hasExtraTimeRow && extraApiRef?.current?.scroll({ left });
    };

    return mainApiRef.current?.subscribeEvent('scrollPositionChange', handleFooterScroll);
  }, [mainApiRef, totalApiRef, extraApiRef, hasExtraTimeRow]);

  return (
    <>
      <TotalDataGrid
        apiRef={totalApiRef}
        columns={totalColumns as GridColDef<GridValidRowModel>[]}
        rows={totalRows}
        // Pinned
        pinnedColumns={{
          left: [TimesheetTotalColumnEnums.TOTAL_ROW_NAME],
          right: [TimesheetTotalColumnEnums.GRAND_TOTAL_HOURS, TimesheetTotalColumnEnums.GRAND_TOTAL_EXPENSES],
        }}
        // Settings
        disableRowSelectionOnClick
        rowSelection={false}
        hideFooter
        // Styles
        columnHeaderHeight={0}
        rowHeight={TIMESHEET_ROW_HEIGHT}
        showColumnVerticalBorder
        showCellVerticalBorder
        // Custom props
        rowsCount={totalRows.length}
      />
      {hasExtraTimeRow && (
        <ExtraDataGrid
          apiRef={extraApiRef}
          columns={extraColumns as GridColDef<GridValidRowModel>[]}
          rows={extraRows}
          // Pinned
          pinnedColumns={{
            left: [TimesheetExtraColumnEnums.EXTRA_ROW_NAME],
            right: [TimesheetExtraColumnEnums.GRAND_TOTAL_HOURS, TimesheetExtraColumnEnums.GRAND_TOTAL_EXPENSES],
          }}
          // Settings
          disableRowSelectionOnClick
          rowSelection={false}
          hideFooter
          // Styles
          columnHeaderHeight={0}
          rowHeight={TIMESHEET_ROW_HEIGHT}
          showColumnVerticalBorder
          showCellVerticalBorder
          // Custom props
        />
      )}
    </>
  );
};

const TotalDataGrid = styled(DataGridPro, {
  shouldForwardProp: (prop) => prop !== 'hasTotalExpensesRow',
})<{ rowsCount?: number }>(({ rowsCount }) => ({
  border: 'none',
  minHeight: (rowsCount ?? 0) * TIMESHEET_ROW_HEIGHT,
  maxHeight: (rowsCount ?? 0) * TIMESHEET_ROW_HEIGHT,
  // GRID
  '.MuiDataGrid-main': {
    borderTop: 'none !important',
    borderRadius: '0 0 8px 8px !important',
  },
  // SCROLLBAR
  '.MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:vertical': {
    background: 'transparent !important',
  },
  '.MuiDataGrid-virtualScroller::-webkit-scrollbar:horizontal': {
    display: 'none',
  },
  // HEADER
  '.MuiDataGrid-columnHeaders': {
    borderBottom: 'none',
  },
  // CELL
  '.MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within,': {
    outline: 'none',
  },
}));

const ExtraDataGrid = styled(DataGridPro)(() => ({
  marginTop: 8,
  border: 'none',
  minHeight: TIMESHEET_ROW_HEIGHT,
  maxHeight: TIMESHEET_ROW_HEIGHT,
  // GRID
  '.MuiDataGrid-main': {
    borderTop: 'none !important',
    borderRadius: '8px !important',
  },
  // SCROLLBAR
  '.MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:vertical': {
    background: 'transparent !important',
  },
  '.MuiDataGrid-virtualScroller::-webkit-scrollbar:horizontal': {
    display: 'none',
  },
  // HEADER
  '.MuiDataGrid-columnHeaders': {
    borderBottom: 'none',
  },
  // CELL
  '.MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within,': {
    outline: 'none',
  },
}));
